import { useEffect, useRef, useState } from "react";
import { ListFilter, Search, SlidersHorizontal, UserSearch, X } from "lucide-react";
import { Permission, useCan } from "@/components/session-provider";
import { TicketQuickSearch } from "@/components/tickets/TicketQuickSearch";
import { getPeople, getPerson, type Person } from "@/lib/people";
import {
  attentionLabel,
  getAssigneeRoster,
  getReference,
  statusLabel,
  type AssignableUser,
  type ReferenceSite,
  type ReferenceTeam,
  type TicketAttention,
  type TicketPriority,
  type TicketStatus,
} from "@/lib/tickets";
import { formatSite } from "@/lib/sites";
import { formatDayLabel } from "@/lib/time";
import { cn } from "@/lib/utils";

/**
 * What the queue is narrowed to, beyond whichever view tab is selected.
 *
 * ⚠️ Ids, never names. Every one of these travels to the server and is applied in SQL on
 * top of the global query filter and row-level security, so a filter can only ever show
 * fewer of the rows the caller was already entitled to. None of it is an access decision.
 *
 * Two of the six are identities rather than ticket metadata, and the server gates those
 * separately: `assigneeUserId` needs `ticket.assign` (D-31) and `requesterUserId` needs
 * `user.manage` unless it is your own. Both are IGNORED rather than refused when the
 * caller lacks the permission — which is why the controls for them are not rendered at
 * all without it. A filter that silently does nothing is worse than no filter.
 */
export interface QueueFilterValue {
  status?: TicketStatus;
  minimumPriority?: TicketPriority;
  teamId?: string;
  siteId?: string;
  assigneeUserId?: string;
  requesterUserId?: string;

  /**
   * When the request was raised, as `YYYY-MM-DD` in THIS READER'S time zone.
   *
   * ⚠️ Held as a day here and in the URL, and converted to a pair of instants only when the
   * request is built. A day is what somebody picked and what the chip has to say back to
   * them; an instant is the only thing the server can compare. Keeping the day means a link
   * pasted to a colleague reads as "raised on the 12th" for them too, rather than as
   * somebody else's midnight.
   */
  raisedFrom?: string;
  raisedTo?: string;

  /**
   * The home screen's two attention buckets, arriving as a filter.
   *
   * ⚠️ There is deliberately no control for it in the panel below — it is set by following a
   * tile, not by building a query. It still appears as a CHIP, because a filter that shortens
   * the queue without saying so is the one failure this whole component exists to prevent:
   * somebody lands on eleven rows where there were sixty and cannot tell a filter from an
   * outage. The chip names it and clears it.
   */
  attention?: TicketAttention;

  /**
   * Free text matched against the request's SUBJECT.
   *
   * ⚠️ The subject only, never the description, decided server-side — see
   * `TicketFilter.SubjectContains`. A subject is the one-line label somebody wrote to route
   * their request; the description is the narrative, and a substring search over narratives
   * is the tool for "find every request mentioning this person".
   *
   * ⚠️ Distinct from `TicketQuickSearch` beside it, which JUMPS to one request by number and
   * leaves the queue alone. This narrows the queue and is a filter like any other, which is
   * why it carries a chip: a shortened queue must always say why.
   *
   * ⚠️ Ungated — it names nobody and describes the requests, exactly as status and priority
   * do — and applied on top of the query filter and RLS, so it returns a subset of what the
   * caller could already list.
   */
  search?: string;
}

/** The server's floor, restated so the browser can refuse rather than send a 400. */
export const MINIMUM_SEARCH_LENGTH = 3;

export function countActiveFilters(value: QueueFilterValue): number {
  return Object.values(value).filter(Boolean).length;
}

/**
 * Searching the queue by subject.
 *
 * ⚠️ IT SEARCHES ON SUBMIT, NEVER WHILE TYPING, and that is not about saving requests.
 * Every row a search returns is recorded as a PHI read against the searcher's name
 * (§164.312(b)), so searching per keystroke files those events for the matches of `p`, `pr`
 * and `pri` on the way to `printer` — against tickets nobody asked to see, and against a
 * person who did nothing wrong. A debounce narrows that window; it does not close it. This
 * is D-33's rule for the number lookup, and it applies here for the same reason.
 *
 * ⚠️ Held locally until submitted, so the box does not fight the URL on every keystroke.
 * The applied term still lives in the URL, which is what makes a narrowed queue shareable.
 */
function SubjectSearch({
  value,
  onSearch,
}: {
  value: string | undefined;
  onSearch: (next: string | undefined) => void;
}) {
  const [term, setTerm] = useState(value ?? "");

  // Follows the URL when it changes underneath — clearing the chip, or landing on a link.
  useEffect(() => setTerm(value ?? ""), [value]);

  const trimmed = term.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MINIMUM_SEARCH_LENGTH;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (tooShort) return;
        onSearch(trimmed === "" ? undefined : trimmed);
      }}
      className="relative"
    >
      <label htmlFor="queue-search" className="sr-only">
        Search subjects
      </label>
      <ListFilter
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        id="queue-search"
        value={term}
        maxLength={200}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Search subjects"
        aria-describedby={tooShort ? "queue-search-hint" : undefined}
        className={cn(
          "h-9 w-44 rounded-lg border bg-background pl-8 pr-8 text-sm transition focus:outline-none",
          tooShort ? "border-warning" : "border-input focus:border-ring",
        )}
      />

      {term && (
        <button
          type="button"
          aria-label="Clear the search"
          onClick={() => {
            setTerm("");
            onSearch(undefined);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}

      {/* ⚠️ Said here rather than sent and refused. The server refuses a shorter term with a
          message (D-60) — it does not silently ignore it, because that would hand back the
          whole readable queue under a heading saying these are the matches. This only means
          somebody finds out while the cursor is still in the box. */}
      {tooShort && (
        <p
          id="queue-search-hint"
          role="status"
          className="absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded-md border border-warning/40 bg-card px-2 py-1 text-xs text-muted-foreground"
        >
          At least {MINIMUM_SEARCH_LENGTH} characters, then press Enter.
        </p>
      )}
    </form>
  );
}

/**
 * ⚠️ The server's six, and nothing else. This list carried a seventh — "Triage" — that the
 * API has never had, and choosing it sent a status the endpoint could not read: the request
 * failed outright and the queue reported that nothing matched, which is a statement about
 * somebody's own data and it was untrue. `TicketStatus` is now exactly the server's enum, so
 * a phantom here is a TypeScript error rather than an empty screen.
 */
const statuses: TicketStatus[] = [
  "New",
  "InProgress",
  "AwaitingUser",
  "OnHold",
  "Resolved",
  "Closed",
];

/** Descending, because "at least High" is the question people ask of a queue. */
const priorities: TicketPriority[] = ["Critical", "Urgent", "High", "Normal", "Low"];

const selectClass =
  "h-9 w-full rounded-lg border border-input bg-background px-2 text-sm transition " +
  "focus:border-ring focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";

export function QueueFilters({
  value,
  onChange,
  /**
   * Why the assignee filter cannot apply under the active view, if it cannot.
   *
   * ⚠️ Two views make it meaningless, in opposite directions. "Unassigned" excludes
   * everybody, so narrowing to a named person can only ever return nothing; "My tickets"
   * resolves the assignee from the SESSION server-side, so a named person would be
   * silently replaced by you. Both would leave a chip on screen naming somebody whose
   * tickets are not what is being shown — a filter that lies is worse than one that is
   * disabled and says why.
   */
  assigneeUnavailable,
}: {
  value: QueueFilterValue;
  onChange: (next: QueueFilterValue) => void;
  assigneeUnavailable?: string;
}) {
  // Rendering decisions only. The server ignores a filter the caller may not use, and
  // re-derives both of these per request — deriving privilege in the browser is defect D1.
  const canFilterByAssignee = useCan(Permission.TicketAssign);
  const canFilterByRequester = useCan(Permission.UserManage);

  const [teams, setTeams] = useState<ReferenceTeam[] | null>(null);
  const [sites, setSites] = useState<ReferenceSite[] | null>(null);
  const [assignees, setAssignees] = useState<AssignableUser[] | null>(null);

  // Open on any breakpoint once something is filtered, so a narrowed queue never looks
  // like a short one with the reason folded away.
  const [open, setOpen] = useState(() => countActiveFilters(value) > 0);

  useEffect(() => {
    let cancelled = false;

    void getReference()
      .then((r) => {
        if (cancelled) return;
        setTeams(r.teams);
        setSites(r.sites);
      })
      .catch(() => {
        if (cancelled) return;
        setTeams([]);
        setSites([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canFilterByAssignee) return;

    let cancelled = false;

    // Gated on ticket.assign server-side. Requested only when the session says the caller
    // holds it, so this is not a 403 the console reports on every queue load.
    void getAssigneeRoster()
      .then((rows) => !cancelled && setAssignees(rows))
      .catch(() => !cancelled && setAssignees([]));

    return () => {
      cancelled = true;
    };
  }, [canFilterByAssignee]);

  const active = countActiveFilters(value);
  const set = (patch: Partial<QueueFilterValue>) => onChange({ ...value, ...patch });

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Beside the filters rather than inside the panel, because it is not one: it
            jumps to a request instead of narrowing the queue, and it has to be reachable
            without opening anything. */}
        <TicketQuickSearch />

        <SubjectSearch value={value.search} onSearch={(next) => set({ search: next })} />

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm transition",
            active > 0
              ? "border-primary/40 bg-primary/10 text-foreground"
              : "border-input hover:bg-accent",
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          Filters
          {active > 0 && (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
              {active}
            </span>
          )}
        </button>

        {active > 0 && (
          <button
            type="button"
            onClick={() => onChange({})}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground transition hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear all
          </button>
        )}

        <ActiveChips
          value={value}
          teams={teams}
          sites={sites}
          assignees={assignees}
          onClear={(patch) => set(patch)}
        />
      </div>

      {open && (
        <div className="mt-3 grid gap-3 rounded-2xl border border-border bg-card p-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Status" htmlFor="filter-status">
            <select
              id="filter-status"
              className={selectClass}
              value={value.status ?? ""}
              onChange={(e) =>
                set({ status: (e.target.value || undefined) as TicketStatus | undefined })
              }
            >
              <option value="">Any status</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {statusLabel[s]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Priority" htmlFor="filter-priority">
            <select
              id="filter-priority"
              className={selectClass}
              value={value.minimumPriority ?? ""}
              onChange={(e) =>
                set({
                  minimumPriority: (e.target.value || undefined) as TicketPriority | undefined,
                })
              }
            >
              {/* "At least", not "exactly". Somebody filtering for Urgent wants the
                  Critical ones too — the opposite is how an emergency gets filtered out
                  of the view meant to surface it. */}
              <option value="">Any priority</option>
              {priorities.map((p) => (
                <option key={p} value={p}>
                  {p} and above
                </option>
              ))}
            </select>
          </Field>

          <Field label="Support team" htmlFor="filter-team">
            <select
              id="filter-team"
              className={selectClass}
              disabled={teams === null}
              value={value.teamId ?? ""}
              onChange={(e) => set({ teamId: e.target.value || undefined })}
            >
              <option value="">{teams === null ? "Loading…" : "Any team"}</option>
              {(teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Site" htmlFor="filter-site">
            <select
              id="filter-site"
              className={selectClass}
              disabled={sites === null}
              value={value.siteId ?? ""}
              onChange={(e) => set({ siteId: e.target.value || undefined })}
            >
              <option value="">{sites === null ? "Loading…" : "Any site"}</option>
              {(sites ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {formatSite(s)}
                </option>
              ))}
            </select>
          </Field>

          {/* When it was RAISED, not when it was touched. "Everything from last week" is a
              question about intake, and matching it against a column that moves every time
              somebody comments would answer it differently each time it was asked.

              ⚠️ Two inputs rather than a range picker: either end alone is a legitimate
              question ("anything since Monday", "anything before the migration"), and a
              picker that demands both turns one of them into two clicks and a guess. */}
          <Field label="Raised from" htmlFor="filter-raised-from">
            <input
              id="filter-raised-from"
              type="date"
              className={selectClass}
              // Stops the range being inverted rather than explaining it afterwards. An
              // inverted range is not an error the server needs to refuse — it matches
              // nothing, honestly — but nothing is a poor answer to a typo.
              max={value.raisedTo}
              value={value.raisedFrom ?? ""}
              onChange={(e) => set({ raisedFrom: e.target.value || undefined })}
            />
          </Field>

          <Field label="Raised to" htmlFor="filter-raised-to" hint="Includes the whole day.">
            <input
              id="filter-raised-to"
              type="date"
              className={selectClass}
              min={value.raisedFrom}
              value={value.raisedTo ?? ""}
              onChange={(e) => set({ raisedTo: e.target.value || undefined })}
            />
          </Field>

          {/* ⚠️ Rendered only for callers holding ticket.assign, because the server ignores
              the filter without it — and an input that quietly does nothing is a worse
              experience than an absent one. The roster behind it is gated on the same
              permission. */}
          {canFilterByAssignee && (
            <Field label="Assignee" htmlFor="filter-assignee" hint={assigneeUnavailable}>
              <select
                id="filter-assignee"
                className={selectClass}
                disabled={assignees === null || assigneeUnavailable !== undefined}
                value={value.assigneeUserId ?? ""}
                onChange={(e) => set({ assigneeUserId: e.target.value || undefined })}
              >
                <option value="">{assignees === null ? "Loading…" : "Anyone"}</option>
                {(assignees ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Same rule, different permission: the server narrows this to your own id
              without user.manage, so offering it to everybody would be offering a control
              that answers a question nobody asked. */}
          {canFilterByRequester && (
            <Field label="Requester">
              <RequesterPicker
                userId={value.requesterUserId}
                onChange={(id) => set({ requesterUserId: id })}
              />
            </Field>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
          {label}
        </label>
      ) : (
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      )}
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * What is currently being hidden, named on screen.
 *
 * ⚠️ The point is not decoration. A queue narrowed by a control that has scrolled out of
 * view is indistinguishable from a queue that is empty, or from an outage — so every
 * active filter says what it is and offers one click out of it.
 *
 * A name is resolved from the id rather than carried in the URL: a colleague's name has no
 * business in a link that gets pasted into a chat window.
 */
function ActiveChips({
  value,
  teams,
  sites,
  assignees,
  onClear,
}: {
  value: QueueFilterValue;
  teams: ReferenceTeam[] | null;
  sites: ReferenceSite[] | null;
  assignees: AssignableUser[] | null;
  onClear: (patch: Partial<QueueFilterValue>) => void;
}) {
  const [requester, setRequester] = useState<Person | null>(null);

  useEffect(() => {
    if (!value.requesterUserId) {
      setRequester(null);
      return;
    }

    let cancelled = false;

    void getPerson(value.requesterUserId)
      .then((p) => !cancelled && setRequester(p))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [value.requesterUserId]);

  const chips: { key: keyof QueueFilterValue; label: string }[] = [];

  // First, because it is the only one that arrives without anybody having opened this panel.
  if (value.attention) {
    chips.push({ key: "attention", label: attentionLabel[value.attention] });
  }

  // ⚠️ A chip like any other. A search shortens the queue, so it has to say so and be
  // clearable in one press — the box itself scrolls out of view on a phone, and a queue that
  // is quietly one-tenth its usual length with nothing on screen explaining why is the
  // failure this whole component exists to prevent.
  if (value.search) chips.push({ key: "search", label: `Subject contains “${value.search}”` });

  if (value.status) chips.push({ key: "status", label: statusLabel[value.status] });
  if (value.minimumPriority) {
    chips.push({ key: "minimumPriority", label: `${value.minimumPriority} and above` });
  }

  if (value.teamId) {
    chips.push({
      key: "teamId",
      label: teams?.find((t) => t.id === value.teamId)?.name ?? "One team",
    });
  }

  if (value.siteId) {
    chips.push({
      key: "siteId",
      label: (() => {
        const site = sites?.find((s) => s.id === value.siteId);
        return site ? formatSite(site) : "One site";
      })(),
    });
  }

  // Each end is its own chip, because each end can be cleared on its own — and a range
  // rendered as one chip cannot say which half somebody wants back.
  if (value.raisedFrom) {
    chips.push({
      key: "raisedFrom",
      label: `Raised on or after ${formatDayLabel(value.raisedFrom)}`,
    });
  }

  if (value.raisedTo) {
    chips.push({ key: "raisedTo", label: `Raised on or before ${formatDayLabel(value.raisedTo)}` });
  }

  if (value.assigneeUserId) {
    chips.push({
      key: "assigneeUserId",
      // "One person" rather than a blank chip while the roster loads, or if it never does.
      label: assignees?.find((a) => a.id === value.assigneeUserId)?.displayName ?? "One assignee",
    });
  }

  if (value.requesterUserId) {
    chips.push({
      key: "requesterUserId",
      label: `Raised by ${requester?.displayName ?? "one person"}`,
    });
  }

  if (chips.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <li key={chip.key}>
          <span className="inline-flex h-7 items-center gap-1 rounded-full border border-primary/30 bg-primary/10 pl-2.5 pr-1 text-xs">
            {chip.label}
            <button
              type="button"
              aria-label={`Remove filter ${chip.label}`}
              onClick={() => onClear({ [chip.key]: undefined })}
              className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground transition hover:bg-background hover:text-foreground"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Which person's requests to show, chosen by searching.
 *
 * ⚠️ A dropdown cannot do this job and trying was a defect elsewhere in this codebase: the
 * roster is keyset-paginated and capped server-side, so a `<select>` built from one page
 * offers a fraction of the organisation and quietly omits everybody else. The term goes to
 * the server, which does the matching.
 */
function RequesterPicker({
  userId,
  onChange,
}: {
  userId: string | undefined;
  onChange: (id: string | undefined) => void;
}) {
  const [chosen, setChosen] = useState<Person | null>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<Person[] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userId) {
      setChosen(null);
      return;
    }

    let cancelled = false;

    void getPerson(userId)
      .then((p) => !cancelled && setChosen(p))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const trimmed = term.trim();

    if (trimmed.length < 2) {
      setResults(null);
      return;
    }

    let cancelled = false;

    // Debounced. This reaches an endpoint that returns named people, and a request per
    // keystroke is both wasteful and noise in the record of who looked up whom.
    const handle = setTimeout(() => {
      void getPeople({ search: trimmed, status: "active", pageSize: 8 })
        .then((r) => !cancelled && setResults(r.items))
        .catch(() => !cancelled && setResults([]));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [term, open]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => {
            setOpen((o) => !o);
            setTerm("");
            setResults(null);
          }}
          aria-expanded={open}
          className="inline-flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-input bg-background px-2 text-sm transition hover:bg-accent"
        >
          <UserSearch className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className={cn("truncate", !userId && "text-muted-foreground")}>
            {userId ? (chosen?.displayName ?? "One person") : "Anyone"}
          </span>
        </button>

        {userId && (
          <button
            type="button"
            aria-label="Clear requester filter"
            onClick={() => onChange(undefined)}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-input transition hover:bg-accent"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 rounded-lg border border-border bg-popover p-2 shadow-lg">
          <label className="sr-only" htmlFor="filter-requester-search">
            Search for a requester
          </label>
          <div className="relative">
            <Search
              className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="filter-requester-search"
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search by name"
              className="h-9 w-full rounded-lg border border-input bg-background pl-7 pr-2 text-base focus:border-ring focus:outline-none md:text-sm"
            />
          </div>

          {term.trim().length > 0 && term.trim().length < 2 && (
            <p className="mt-1.5 text-xs text-muted-foreground">Keep typing.</p>
          )}

          {results !== null && results.length === 0 && (
            <p className="mt-1.5 text-xs text-muted-foreground">Nobody here matches.</p>
          )}

          {results !== null && results.length > 0 && (
            <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(p.id);
                      setOpen(false);
                      setTerm("");
                      setResults(null);
                    }}
                    className="w-full rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-accent"
                  >
                    <span className="block truncate">{p.displayName}</span>
                    {p.jobTitle && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {p.jobTitle}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The empty state for a queue that is empty only because of the filters on it. */
export function NoFilterMatches({ onClear }: { onClear: () => void }) {
  return (
    <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center">
      <ListFilter className="h-9 w-9 text-muted-foreground" aria-hidden />
      <p className="text-sm font-medium">Nothing matches these filters</p>
      <p className="max-w-xs text-sm text-muted-foreground">
        The queue itself may not be empty — these filters are hiding everything in it.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-3 text-sm transition hover:bg-accent"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
        Clear filters
      </button>
    </div>
  );
}
